<!-- Rendered in the app header's center slot (HeaderContentService) when it
     fits there on one line, else at the top of the page (menuInHeader).
     Either way bindings resolve against this component, and the scoped
     styles apply because the template is declared here. -->
<ng-template #headerMenu>
    <div class="header-page-actions">
        <div class="actions-start">
            <span class="page-name">รายได้และค่าธรรมเนียม</span>
            <dx-date-box
                class="month-picker"
                type="date"
                displayFormat="MMM yyyy"
                [calendarOptions]="monthPickerOptions"
                [openOnFieldClick]="true"
                [acceptCustomValue]="false"
                [showClearButton]="false"
                [width]="120"
                hint="เดือนแรกของช่วง"
                [(value)]="rangeFrom"
                [disabled]="isLoading || isSyncing"
                (onValueChanged)="onRangeChanged('from')"
            ></dx-date-box>
            <span class="range-sep">ถึง</span>
            <dx-date-box
                class="month-picker"
                type="date"
                displayFormat="MMM yyyy"
                [calendarOptions]="monthPickerOptions"
                [openOnFieldClick]="true"
                [acceptCustomValue]="false"
                [showClearButton]="false"
                [width]="120"
                hint="เดือนสุดท้ายของช่วง"
                [(value)]="rangeTo"
                [disabled]="isLoading || isSyncing"
                (onValueChanged)="onRangeChanged('to')"
            ></dx-date-box>
            <dx-select-box
                [items]="periodBasisSelection"
                [(value)]="selectedBasis"
                valueExpr="id"
                displayExpr="text"
                [width]="230"
                hint="วันที่ที่ใช้จัดรายการเข้าช่วง"
                [disabled]="isLoading || isSyncing"
                (onValueChanged)="onEditorValueChanged()"
            ></dx-select-box>
            <dx-tag-box
                [items]="entitySelection"
                [(value)]="selectedEntityIds"
                valueExpr="id"
                displayExpr="text"
                placeholder="ผู้เสียภาษี (ทั้งหมด)"
                [width]="170"
                [multiline]="false"
                [showSelectionControls]="true"
                [disabled]="isLoading || isSyncing"
                (onValueChanged)="onEditorValueChanged()"
            ></dx-tag-box>
            <dx-tag-box
                [items]="storeSelection"
                [(value)]="selectedStoreIds"
                valueExpr="id"
                displayExpr="text"
                placeholder="ร้าน (ทั้งหมด)"
                [width]="150"
                [multiline]="false"
                [showSelectionControls]="true"
                [disabled]="isLoading || isSyncing"
                (onValueChanged)="onEditorValueChanged()"
            ></dx-tag-box>
            <dx-button
                text="แสดง"
                [type]="isEditorsDirty ? 'default' : 'normal'"
                [stylingMode]="isEditorsDirty ? 'contained' : 'outlined'"
                [disabled]="isLoading || isSyncing || isVerifying"
                (onClick)="load()"
            ></dx-button>
        </div>
        <div class="actions-end">
            <dx-button
                text="ตรวจสอบความครบถ้วน"
                icon="check"
                stylingMode="outlined"
                hint="ถามแพลตฟอร์มว่าช่วงนี้มีกี่รายการ แล้วเทียบกับที่เก็บไว้"
                [disabled]="isLoading || isSyncing || isVerifying"
                (onClick)="verifyNow()"
            ></dx-button>
            <dx-button
                text="ซิงก์จากแพลตฟอร์ม"
                icon="refresh"
                stylingMode="outlined"
                hint="ดึงทุกเดือนในช่วงที่เลือก ทีละเดือน — เดือนละหลายนาที"
                [disabled]="isLoading || isSyncing || isVerifying"
                (onClick)="syncNow()"
            ></dx-button>
            <dx-button
                icon="xlsxfile"
                hint="Export .xlsx"
                stylingMode="outlined"
                [disabled]="isLoading || isSyncing"
                (onClick)="onExporting()"
            ></dx-button>
        </div>
    </div>
</ng-template>

<div class="main dx-card padding-content">
    @if (!menuInHeader()) {
        <div class="in-page-menu" #inPageMenu>
            <ng-container [ngTemplateOutlet]="headerMenu"></ng-container>
        </div>
    }

    <!-- KPI tiles -->
    <div class="kpi-row">
        <!-- The headline is the VAT base, not what buyers paid: this page
             exists for ภ.พ.30 and the ฿1.8M watch, and both run on the
             consideration the seller receives for the supply — including the
             part a platform voucher paid instead of the buyer. -->
        <div class="kpi">
            <div class="kpi-label">
                ยอดขาย (ฐานภาษี)
                <span
                    class="kpi-sub"
                    title="ฐานภาษี = ราคาสินค้าหักส่วนลดที่ร้านออกเอง บวกค่าส่งที่ลูกค้าจ่าย — รวมคูปอง/Coins/โปรโมชันที่แพลตฟอร์มออกให้ เพราะร้านยังได้รับเงินเต็ม ภาษีขายและเพดาน ฿1.8M คิดจากตัวเลขนี้ · ลูกค้าจ่าย = เงินที่ลูกค้าจ่ายเอง หลังหักส่วนที่แพลตฟอร์มออกให้"
                    >ลูกค้าจ่าย {{ totalBuyerTotal | formatNumber: 2 }}</span
                >
                @if (totalTaxableAfterRefunds !== null) {
                    <span
                        class="kpi-sub"
                        title="ฐานภาษีหักเงินที่คืนลูกค้าแล้ว ({{
                            totalRefunds | formatNumber: 2
                        }}) — ตัวเลขประกอบ ยอดหลักยังเป็นก่อนหักคืน"
                        >หลังหักคืนสินค้า
                        {{ totalTaxableAfterRefunds | formatNumber: 2 }}</span
                    >
                }
            </div>
            <div class="kpi-value">
                {{ totalTaxable | formatNumber: 2 }}
            </div>
        </div>
        <div class="kpi">
            <div class="kpi-label">
                ค่าธรรมเนียมแพลตฟอร์ม
                <span class="kpi-sub"
                    >{{ effectiveFeePct | formatNumber: 2 }}% ของยอดขาย</span
                >
            </div>
            <div class="kpi-value negative">
                {{ totalFees | formatNumber: 2 }}
            </div>
        </div>
        <div class="kpi">
            <div class="kpi-label">เงินเข้าจริง (settlement)</div>
            <div class="kpi-value">
                {{ totalSettlement | formatNumber: 2 }}
            </div>
        </div>
        <div class="kpi">
            <div class="kpi-label">
                ภาษีขาย (output VAT)
                <span
                    class="kpi-sub"
                    title="ยังไม่หักสินค้าที่คืน — ออเดอร์ที่คืนเงินต้องออกใบลดหนี้เอง"
                    >ก่อนหักคืนสินค้า</span
                >
                @if (totalOutputVatAfterRefunds !== null) {
                    <span
                        class="kpi-sub"
                        title="ถ้าออกใบลดหนี้ให้ทุกออเดอร์ที่คืนเงินแล้ว ภาษีขายจะเหลือเท่านี้ (7/107 ของยอดคืน เฉพาะแถวที่มี VAT)"
                        >หลังหักคืนสินค้า ≈
                        {{ totalOutputVatAfterRefunds | formatNumber: 2 }}</span
                    >
                }
            </div>
            <div class="kpi-value">
                {{ totalOutputVat | formatNumber: 2 }}
            </div>
        </div>
        <div class="kpi">
            <div class="kpi-label">
                ภาษีซื้อจากค่าธรรมเนียม
                <span
                    class="kpi-sub"
                    title="เฉพาะผู้เสียภาษีที่จด VAT · Lazada = VAT ที่แจ้งในรายการค่าธรรมเนียม · Shopee/TikTok = ประมาณ 7/107 ของค่าธรรมเนียมที่แพลตฟอร์มคิด VAT (คอมมิชชั่น ธุรกรรม บริการ; ไม่รวมค่าส่งและค่า affiliate ของ TikTok) — ขอคืนได้จริงตามใบกำกับภาษีที่แพลตฟอร์มออกในชื่อผู้เสียภาษีเท่านั้น"
                    >ประมาณการ</span
                >
            </div>
            <div class="kpi-value positive">
                {{ totalInputVatOnFees | formatNumber: 2 }}
            </div>
        </div>
    </div>

    <!-- ฿1.8M threshold watch, per taxpayer -->
    @if (thresholdRows.length) {
        <div class="panel">
            <div class="panel-title">
                รายได้สะสมปี {{ loadedYear }} เทียบเพดาน VAT ฿1.8M
            </div>
            @for (row of thresholdRows; track row.entityId) {
                <div class="threshold-row">
                    <div class="threshold-name">
                        {{ row.legalName }}
                        @if (row.vatRegistered) {
                            <span class="chip chip-ok">จด VAT แล้ว</span>
                        }
                    </div>
                    <div class="threshold-bar">
                        <div
                            class="threshold-fill"
                            [class.warn]="row.state === 'warn'"
                            [class.over]="row.state === 'over'"
                            [style.width.%]="row.pctOfThreshold"
                        ></div>
                        <div class="threshold-marker"></div>
                    </div>
                    <div class="threshold-figures">
                        <span>{{ row.revenue | formatNumber: 2 }}</span>
                        @if (row.afterRefunds !== null) {
                            <span
                                class="muted"
                                title="รายได้สะสมหักเงินที่คืนลูกค้าแล้ว — แถบและค่าคาดทั้งปียังใช้ยอดก่อนหักคืน"
                                >หลังหักคืน
                                {{ row.afterRefunds | formatNumber: 2 }}</span
                            >
                        }
                        <span class="muted" [title]="row.projectionNote"
                            >คาดทั้งปี
                            {{ row.projected | formatNumber: 0 }}</span
                        >
                    </div>
                </div>
                @if (row.months.length) {
                    <div class="threshold-months">
                        @for (m of row.months; track m.key) {
                            <span
                                class="threshold-month"
                                [class.partial]="m.partial"
                                [title]="
                                    m.partial
                                        ? 'ยอดต่ำผิดปกติ — ควรตรวจสอบ (ไม่นับในค่าคาดทั้งปี)'
                                        : ''
                                "
                                >{{ m.label }}
                                {{ m.value | formatNumber: 0 }}</span
                            >
                        }
                        @if (row.projectionNote) {
                            <span class="threshold-month note"
                                >คาดทั้งปี = {{ row.projectionNote }}</span
                            >
                        }
                    </div>
                }
            }
            <div class="panel-note">
                เส้นประ = ฿1.5M (เตือนล่วงหน้า) · เพดาน ฿1.8M · เกินเพดานต้องจด
                VAT ภายใน 30 วัน · นับทั้งปีปฏิทินตาม tax point (วันสั่งซื้อ)
                ไม่ขึ้นกับเดือนที่เลือก · คาดทั้งปี = เฉลี่ยเดือนที่ครบแล้ว × 12
                โดยไม่นับเดือนที่ไม่มียอด หรือยอดต่ำกว่าครึ่งของเดือนปกติ
                (ยอดต่ำผิดปกติ — ควรตรวจสอบ อาจซิงก์ไม่ครบหรือขายได้น้อยจริง
                เดือนที่ไม่นับแสดงเป็นสีส้มขีดเส้นประ)
            </div>
        </div>
    }

    <!-- Completeness against the platforms themselves. The only thing on this
         page that can tell a quiet month from a sync that died halfway. -->
    @if (verifyReports) {
        <div class="panel" [class.panel-warn]="!verifyAllOk">
            <div class="panel-title">
                @if (verifyAllOk) {
                    ✓ ตรวจสอบ {{ verifyPeriodLabel }} แล้ว —
                    ข้อมูลครบตรงกับแพลตฟอร์ม
                } @else {
                    ⚠ ตรวจสอบ {{ verifyPeriodLabel }} แล้ว — พบความไม่ตรงกัน
                }
            </div>
            <table class="rollup">
                <thead>
                    <tr>
                        <th>ร้าน</th>
                        <th class="num">ที่เก็บไว้</th>
                        <th class="num">แพลตฟอร์มแจ้ง</th>
                        <th>ผล</th>
                    </tr>
                </thead>
                <tbody>
                    @for (row of verifyRows; track row.storeId) {
                        <tr>
                            <td>{{ row.title }}</td>
                            <td class="num">
                                {{
                                    row.platform !== null
                                        ? row.stored
                                        : (row.storedMoney | formatNumber: 2)
                                }}
                            </td>
                            <td class="num">
                                {{
                                    row.platform !== null
                                        ? row.platform
                                        : row.platformMoney !== null
                                          ? (row.platformMoney
                                            | formatNumber: 2)
                                          : "—"
                                }}
                            </td>
                            <td
                                [class.verdict-ok]="row.state === 'ok'"
                                [class.verdict-bad]="
                                    row.state === 'short' ||
                                    row.state === 'over' ||
                                    row.state === 'error'
                                "
                            >
                                {{ row.state === "ok" ? "✓ " : ""
                                }}{{ row.detail }}
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
            <div class="panel-note">
                เทียบตามวันเงินเข้า (settlement) เสมอ
                เพราะแพลตฟอร์มจัดทำดัชนีตามวันจ่ายเงินของตัวเอง · TikTok
                ไม่มีดัชนีรายออเดอร์ จึงเทียบด้วยยอดเงิน
            </div>
        </div>
    }

    <!-- What the chosen date basis cannot show. Loud, because the failure mode
         it prevents is an empty grid that reads as "no sales". -->
    @if (basisGapsUnknown) {
        <div class="panel panel-warn">
            <div class="panel-title">
                ⚠ ตรวจไม่ได้ว่ามุมมองนี้ซ่อนแถวไว้หรือไม่
            </div>
            <div>
                นับแถวที่ไม่มี
                {{
                    loadedBasis === "taxPointDate" ? "tax point" : "วันสั่งซื้อ"
                }}
                ไม่สำเร็จ — ยอดรวมด้านบนอาจยังไม่ครบ กด "แสดง" อีกครั้ง
                หรือดูตามเกณฑ์เงินเข้า ซึ่งทุกแถวมี
            </div>
        </div>
    }
    @if (basisGaps.length) {
        <div class="panel panel-warn">
            <div class="panel-title">
                ⚠ {{ basisGapTotal }} แถวในช่วงนี้ไม่มีวันที่ตามเกณฑ์ที่เลือก
            </div>
            <div>
                แถวเหล่านี้มีวันเงินเข้าอยู่ในช่วง แต่ไม่มี
                {{
                    loadedBasis === "taxPointDate" ? "tax point" : "วันสั่งซื้อ"
                }}
                จึงไม่ปรากฏในมุมมองนี้เลย — ยอดรวมด้านบนจึงยังไม่ครบ
            </div>
            @for (gap of basisGaps; track gap.storeId) {
                <div class="warn-row">
                    <strong>{{ storeTitle(gap.storeId) }}</strong>
                    <span class="warn-item"
                        >{{ gap.hidden }} จาก {{ gap.stored }} แถว</span
                    >
                </div>
            }
            <div class="panel-note">
                รายการที่ไม่มี tax point (วันสั่งซื้อ)
                เป็นข้อมูลที่ยังไม่สมบูรณ์ — ซิงก์เดือนนั้นใหม่เพื่อเติมข้อมูล
                หรือใช้เกณฑ์ "Settlement" เพื่อดูครบทุกร้าน
            </div>
        </div>
    }

    <!-- Anything that makes the totals above less trustworthy than they look -->
    @if (syncWarnings.length) {
        <div class="panel panel-warn">
            <div class="panel-title">ข้อควรตรวจสอบจากการซิงก์ล่าสุด</div>
            @for (report of syncWarnings; track report.title) {
                <div class="warn-row">
                    <strong>{{ report.title }}</strong>
                    @if (report.error) {
                        <span class="warn-item"
                            >ดึงข้อมูลไม่สำเร็จ: {{ report.error }}</span
                        >
                    }
                    @if (report.reconciliationFailures) {
                        <span class="warn-item"
                            >{{ report.reconciliationFailures }} รายการ
                            ค่าธรรมเนียมไม่ตรงกับยอดที่แพลตฟอร์มจ่ายจริง</span
                        >
                    }
                    @if (report.missingTaxPoint) {
                        <span class="warn-item"
                            >{{ report.missingTaxPoint }} รายการไม่มี tax point
                            (วันสั่งซื้อ) — ไม่ถูกนับในมุมมอง tax point</span
                        >
                    }
                    @if (report.duplicateIds) {
                        <span class="warn-item"
                            >⚠ {{ report.duplicateIds }} รายการซ้ำรหัสเดียวกัน —
                            ยอดอาจหายไป ต้องตรวจสอบ</span
                        >
                    }
                    @if (report.unmappedFeeNames?.length) {
                        <span class="warn-item"
                            >ค่าธรรมเนียมชนิดใหม่:
                            {{ report.unmappedFeeNames?.join(", ") }}</span
                        >
                    }
                    @if (report.unjoinedOrders) {
                        <span class="warn-item"
                            >⚠ {{ report.unjoinedOrders }} รายการ Shopee
                            ไม่ได้ข้อมูลออเดอร์ (วันสั่งซื้อ) จึงไม่ได้บันทึก —
                            ซิงก์เดือนนี้ซ้ำ</span
                        >
                    }
                    @if (report.unparsedAmounts) {
                        <span class="warn-item"
                            >⚠
                            {{ report.unparsedAmounts }}
                            รายการอ่านจำนวนเงินไม่ได้ (นับเป็น 0) —
                            ยอดขาย/ค่าธรรมเนียมขาดไป ต้องตรวจสอบ</span
                        >
                    }
                </div>
            }
        </div>
    }

    <!-- Per-store rollup: effective fee rate is the pricing signal -->
    @if (storeRollups.length) {
        <div class="panel">
            <div class="panel-title">สรุปตามร้าน</div>
            <table class="rollup">
                <thead>
                    <tr>
                        <th>ร้าน</th>
                        <th class="num">ออเดอร์</th>
                        <th
                            class="num"
                            title="ฐานภาษี — รวมส่วนที่แพลตฟอร์มออกให้"
                        >
                            ยอดขาย
                        </th>
                        <th class="num" title="เงินที่ลูกค้าจ่ายเอง">
                            ลูกค้าจ่าย
                        </th>
                        <th class="num">คอมมิชชั่น</th>
                        <th class="num">ธุรกรรม</th>
                        <th class="num">บริการ</th>
                        <th class="num">affiliate</th>
                        <th class="num">ค่าส่งที่ร้านออก</th>
                        <th class="num">อื่น ๆ</th>
                        <th class="num">รวมค่าธรรมเนียม</th>
                        <th class="num">% ของยอดขาย</th>
                        <th class="num">เงินเข้าจริง</th>
                    </tr>
                </thead>
                <tbody>
                    @for (row of storeRollups; track row._id) {
                        <tr>
                            <td>{{ storeTitle(row._id) }}</td>
                            <td class="num">{{ row.orders }}</td>
                            <td class="num">
                                {{ taxableFor(row) | formatNumber: 2 }}
                            </td>
                            <td class="num">
                                {{ row.buyerTotal | formatNumber: 2 }}
                            </td>
                            <td class="num">
                                {{ row.feeCommission | formatNumber: 2 }}
                            </td>
                            <td class="num">
                                {{ row.feeTransaction | formatNumber: 2 }}
                            </td>
                            <td class="num">
                                {{ row.feeService | formatNumber: 2 }}
                            </td>
                            <td class="num">
                                {{ row.feeAffiliate | formatNumber: 2 }}
                            </td>
                            <td class="num">
                                {{ row.feeShipping | formatNumber: 2 }}
                            </td>
                            <td class="num">
                                {{ row.feeOther | formatNumber: 2 }}
                            </td>
                            <td class="num strong">
                                {{ row.feeTotal | formatNumber: 2 }}
                            </td>
                            <td class="num strong">
                                {{ effectiveFeePctFor(row) | formatNumber: 2 }}%
                            </td>
                            <td class="num">
                                {{ row.netSettlement | formatNumber: 2 }}
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    }

    <!-- The grid query is capped server-side (finance-tx: 5,000 rows) while
         the KPIs and rollups above aggregate every row. Say so when a range
         exceeds the cap, or the grid footer and the export silently
         disagree with the tiles. -->
    @if (gridTruncated) {
        <div class="panel panel-warn">
            <div class="panel-title">
                ⚠ ตารางแสดง {{ dataSource.length | formatNumber: 0 }} จาก
                {{ totalRows | formatNumber: 0 }} แถว
            </div>
            <div>
                ยอดรวมท้ายตารางและไฟล์ Export จึงไม่ครบ — ตัวเลขด้านบน (KPI,
                สรุปตามร้าน, เพดาน VAT) ครบทุกแถว
                ลดช่วงเดือนหรือกรองร้านเพื่อดูรายการครบ
            </div>
        </div>
    }

    <dx-data-grid
        #dataGrid
        [dataSource]="dataSource"
        [showBorders]="true"
        [columnAutoWidth]="true"
        [allowColumnResizing]="true"
        [allowColumnReordering]="true"
        [rowAlternationEnabled]="true"
        keyExpr="_id"
    >
        <dxo-filter-row [visible]="true"></dxo-filter-row>
        <dxo-header-filter [visible]="true"></dxo-header-filter>
        <dxo-group-panel [visible]="true"></dxo-group-panel>
        <!-- Store groups start collapsed: the group rows carry each store's
             totals, so the page opens on a per-store overview. The Excel
             export still writes every row. -->
        <dxo-grouping [autoExpandAll]="false"></dxo-grouping>
        <dxo-column-chooser [enabled]="true" mode="select"></dxo-column-chooser>
        <dxo-paging [pageSize]="50"></dxo-paging>
        <dxo-pager
            [showPageSizeSelector]="true"
            [allowedPageSizes]="[25, 50, 100, 200]"
            [showInfo]="true"
        ></dxo-pager>

        <dxi-column dataField="storeId" caption="ร้าน" [groupIndex]="0">
        </dxi-column>
        <dxi-column dataField="entityId" caption="ผู้เสียภาษี"></dxi-column>
        <dxi-column dataField="orderSn" caption="เลขออเดอร์"></dxi-column>
        <!-- Lazada ledger month / TikTok statement: one order can hold
             several rows, one per payout. -->
        <dxi-column
            dataField="part"
            caption="รอบจ่าย"
            [visible]="false"
        ></dxi-column>
        <dxi-column
            dataField="taxPointDate"
            caption="Tax point (วันสั่งซื้อ)"
            dataType="datetime"
            format="dd/MM/yy HH:mm"
            [calculateCellValue]="taxPointAsDate"
            cellTemplate="epochTemplate"
        ></dxi-column>
        <dxi-column
            dataField="settlementDate"
            caption="วันเงินเข้า"
            dataType="datetime"
            format="dd/MM/yy HH:mm"
            [calculateCellValue]="settlementAsDate"
            cellTemplate="epochTemplate"
        ></dxi-column>
        <!-- The VAT base, shown for every row — unlike vat.base, which is
             null for a taxpayer who owed no VAT at the tax point. -->
        <dxi-column
            dataField="gross.taxableConsideration"
            caption="ยอดขาย (ฐานภาษี)"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="gross.buyerTotal"
            caption="ลูกค้าจ่าย"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="gross.sellerDiscount"
            caption="ส่วนลดร้าน"
            format="#,##0.00"
            [visible]="false"
        ></dxi-column>
        <dxi-column
            dataField="gross.platformSubsidy"
            caption="ส่วนลดแพลตฟอร์ม"
            format="#,##0.00"
            [visible]="false"
        ></dxi-column>
        <dxi-column
            dataField="fees.commission"
            caption="คอมมิชชั่น"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="fees.transaction"
            caption="ค่าธุรกรรม"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="fees.service"
            caption="ค่าบริการ"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="fees.affiliate"
            caption="Affiliate"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="fees.shippingBorne"
            caption="ค่าส่งที่ร้านออก"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="fees.other"
            caption="อื่น ๆ"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="fees.total"
            caption="รวมค่าธรรมเนียม"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="netSettlement"
            caption="เงินเข้าจริง"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="vat.base"
            caption="ฐานภาษี"
            format="#,##0.00"
            [visible]="false"
        ></dxi-column>
        <dxi-column
            dataField="vat.output"
            caption="ภาษีขาย"
            format="#,##0.00"
        ></dxi-column>
        <dxi-column
            dataField="feeVat.input"
            caption="ภาษีซื้อค่าธรรมเนียม (ประมาณ)"
            format="#,##0.00"
        ></dxi-column>

        <div *dxTemplate="let cell of 'epochTemplate'">
            {{ cell.value ? (cell.value | date: "dd/MM/yy HH:mm") : "—" }}
        </div>

        <dxo-summary>
            <dxi-total-item
                column="gross.taxableConsideration"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="{0}"
            ></dxi-total-item>
            <dxi-total-item
                column="gross.buyerTotal"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="{0}"
            ></dxi-total-item>
            <dxi-total-item
                column="fees.total"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="{0}"
            ></dxi-total-item>
            <dxi-total-item
                column="netSettlement"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="{0}"
            ></dxi-total-item>
            <dxi-total-item
                column="vat.output"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="{0}"
            ></dxi-total-item>
            <dxi-total-item
                column="feeVat.input"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="{0}"
            ></dxi-total-item>

            <dxi-group-item
                column="gross.taxableConsideration"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="ฐานภาษี {0}"
                [alignByColumn]="true"
            ></dxi-group-item>
            <dxi-group-item
                column="gross.buyerTotal"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="ลูกค้าจ่าย {0}"
                [alignByColumn]="true"
            ></dxi-group-item>
            <dxi-group-item
                column="fees.total"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="ค่าธรรมเนียม {0}"
                [alignByColumn]="true"
            ></dxi-group-item>
            <dxi-group-item
                column="netSettlement"
                summaryType="sum"
                valueFormat="#,##0.00"
                displayFormat="เงินเข้า {0}"
                [alignByColumn]="true"
            ></dxi-group-item>
        </dxo-summary>
    </dx-data-grid>
</div>
